import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, type BreadcrumbItem } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes, PermissionScopes } from '../../auth/permissions';
import styles from './ToolsPage.module.css';

export const ToolsPage: React.FC = () => {
  const { t } = useTranslation(['tools', 'nav']);
  const { hasPermission } = useAuth();
  const canViewApiTools = hasPermission(PermissionScopes.apiToolsRead);
  const canViewDataTools = hasPermission(PermissionCodes.knowledgeManage);
  const canViewMcpTools = hasPermission(PermissionScopes.mcpConnectorsRead);
  const canViewToolPlugins = hasPermission(PermissionScopes.aiToolPluginsRead);
  const canViewTransfer = hasPermission(PermissionScopes.aiConfigRead);
  const canViewAgentExtras = canViewTransfer;
  const showToolTabs = canViewApiTools || canViewDataTools || canViewMcpTools || canViewToolPlugins || canViewAgentExtras;

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('ai', { ns: 'nav' }), path: '/ai/knowledge' },
    { label: t('title'), path: '/ai/tools' },
  ];

  return (
    <div className="company-admin-page">
      <Breadcrumb items={breadcrumbItems} />

      <div className={`company-page-head ${styles.pageHeadRow}`}>
        <div className={styles.pageHeadMain}>
          <h2>{t('title')}</h2>
          <p className={styles.pageIntro}>{t('subtitle')}</p>
        </div>
        {canViewTransfer ? (
          <nav className={styles.pageHeadUtility} aria-label={t('transferTabsLabel')}>
            <NavLink
              to="/ai/tools/transfer"
              className={({ isActive }) => [
                styles.transferTab,
                isActive ? styles.transferTabActive : '',
              ].filter(Boolean).join(' ')}
              title={t('tabTransferHint')}
            >
              <span className={styles.transferTabIcon} aria-hidden="true">↕</span>
              <span>{t('tabTransfer')}</span>
            </NavLink>
          </nav>
        ) : null}
      </div>

      {showToolTabs ? (
        <nav className={styles.toolTabBar} aria-label={t('toolTabsLabel')}>
          {canViewApiTools ? (
            <NavLink
              to="/ai/tools/api"
              className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
            >
              {t('tabApi')}
            </NavLink>
          ) : null}
          {canViewDataTools ? (
            <NavLink
              to="/ai/tools/data"
              className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
            >
              {t('tabData')}
            </NavLink>
          ) : null}
          {canViewMcpTools ? (
            <NavLink
              to="/ai/tools/mcp"
              className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
            >
              {t('tabMcp')}
            </NavLink>
          ) : null}
          {canViewToolPlugins ? (
            <NavLink
              to="/ai/tools/plugins"
              className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
            >
              {t('tabPlugins')}
            </NavLink>
          ) : null}
          {canViewAgentExtras ? (
            <>
              <NavLink
                to="/ai/tools/skills"
                className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
              >
                {t('tabSkills')}
              </NavLink>
              <NavLink
                to="/ai/tools/commands"
                className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
              >
                {t('tabCommands')}
              </NavLink>
              <NavLink
                to="/ai/tools/hooks"
                className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
              >
                {t('tabHooks')}
              </NavLink>
              <NavLink
                to="/ai/tools/rules"
                className={({ isActive }) => `${styles.tab} ${isActive ? styles.tabActive : ''}`}
              >
                {t('tabRules')}
              </NavLink>
            </>
          ) : null}
        </nav>
      ) : null}

      <div className={styles.tabPanel}>
        <Outlet />
      </div>
    </div>
  );
};
